import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fmtDate, fmtNum } from "@/lib/format";
import { toast } from "sonner";

// Super Admin: edit each batch quantity of one product at one branch.
export function StockEditDialog({ row, onClose, onDone }) {
  const [batches, setBatches] = useState([]);
  const [qty, setQty] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!row) { return; }
    api.get("/batches", { params: { branch_id: row.branch_id, product_id: row.product_id } }).then((r) => {
      setBatches(r.data); setQty(Object.fromEntries(r.data.map((b) => [b.id, String(b.quantity_on_hand)])));
    }).catch((e) => toast.error(apiError(e)));
  }, [row]);
  const changed = batches.filter((b) => qty[b.id] !== "" && Number(qty[b.id]) !== b.quantity_on_hand);
  const invalid = batches.some((b) => qty[b.id] === "" || !Number.isInteger(Number(qty[b.id])) || Number(qty[b.id]) < 0);
  const total = batches.reduce((s, b) => s + (Number(qty[b.id]) || 0), 0);
  const save = async () => {
    setBusy(true);
    try {
      for (const b of changed) await api.post("/stock/adjust", { batch_id: b.id, new_quantity: Number(qty[b.id]), reason: "Edited from Stock on Hand" });
      toast.success(`Updated ${changed.length} batch(es)`); onDone(); onClose();
    } catch (e) { toast.error(apiError(e)); onDone(); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="stock-edit-dialog">
        <DialogHeader>
          <DialogTitle>{row?.generic_name}</DialogTitle>
          <DialogDescription>{row?.branch_name} · change any batch quantity{row?.unit_of_measure ? ` (per ${row.unit_of_measure})` : ""}. Every change is saved in the audit log.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[55vh] overflow-y-auto border rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-muted text-xs text-muted-foreground text-left sticky top-0"><tr><th className="p-2">Batch</th><th className="p-2">Expiry</th><th className="p-2 text-right">Current</th><th className="p-2 text-right">New quantity</th></tr></thead>
            <tbody>
              {batches.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">No batches</td></tr>}
              {batches.map((b) => (
                <tr key={b.id} className="border-t">
                  <td className="p-2 font-mono text-xs">{b.batch_no}</td>
                  <td className="p-2 text-xs">{fmtDate(b.expiry_date)}</td>
                  <td className="p-2 text-right font-mono">{fmtNum(b.quantity_on_hand)}</td>
                  <td className="p-2 text-right"><Input type="number" min={0} step={1} className="h-8 w-28 ml-auto text-right" value={qty[b.id] ?? ""}
                    onChange={(e) => setQty((q) => ({ ...q, [b.id]: e.target.value }))} data-testid={`stock-edit-qty-${b.batch_no}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter className="items-center gap-2">
          <span className="text-sm text-muted-foreground mr-auto" data-testid="stock-edit-total">New total: <b className="font-mono">{fmtNum(total)}</b></span>
          <Button variant="outline" onClick={onClose} data-testid="stock-edit-cancel">Cancel</Button>
          <Button onClick={save} disabled={busy || invalid || !changed.length} data-testid="stock-edit-save">Save {changed.length ? `${changed.length} change(s)` : ""}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
